import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { catchError, debounceTime, EMPTY, finalize, merge, of, Subject, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { PricePipe } from '@/common/pipes/price.pipe';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { getPolishPaginatorIntl } from '@/common/get-polish-paginator-intl';
import {
	DriversLedgerApiService,
	DriverLedgerSearchPage,
	DriverLedgerSearchRow,
} from '../_api/drivers-ledger.api.service';
import { DriverLedgerApiService } from '@/drivers/driver-ledger/_api/driver-ledger.api.service';
import { DriverLedgerFormComponent } from '@/drivers/driver-ledger/driver-ledger-form/driver-ledger-form.component';
import { DriverLedgerDeleteConfirmComponent } from '@/drivers/driver-ledger/driver-ledger-list/driver-ledger-delete-confirm.component';
import {
	DriverLedgerEntry,
	DriverLedgerFormDialogData,
	DriverLedgerFormDialogResult,
	LEDGER_ENTRY_STATUS_LABEL,
} from '@/drivers/driver-ledger/_models/driver-ledger.model';

@Component({
	selector: 'app-driver-ledger-search-results',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		MatTableModule,
		MatButtonModule,
		MatProgressSpinnerModule,
		MatPaginatorModule,
		MatIconModule,
		MatTooltipModule,
		MatChipsModule,
		DecimalPipe,
		PricePipe,
	],
	providers: [{ provide: MatPaginatorIntl, useValue: getPolishPaginatorIntl() }],
	templateUrl: './driver-ledger-search-results.component.html',
	styleUrl: './driver-ledger-search-results.component.scss',
})
export class DriverLedgerSearchResultsComponent {
	readonly query = input.required<string>();

	readonly displayedColumns = [
		'date',
		'driver',
		'amount',
		'category',
		'vehicle',
		'hasOriginalDocument',
		'description',
		'status',
		'actions',
	];
	readonly statusLabel: Record<string, string> = LEDGER_ENTRY_STATUS_LABEL;

	readonly rows = signal<DriverLedgerSearchRow[]>([]);
	readonly totalItems = signal(0);
	readonly currentPage = signal(1);
	readonly pageSize = signal(20);
	readonly isLoading = signal(false);
	readonly errorMessage = signal<string | null>(null);

	readonly matData = new MatTableDataSource<DriverLedgerSearchRow>([]);

	private readonly pageTrigger$ = new Subject<void>();

	private readonly api = inject(DriversLedgerApiService);
	private readonly entryApi = inject(DriverLedgerApiService);
	private readonly dialog = inject(MatDialog);
	private readonly infobox = inject(InfoboxService);
	private readonly destroyRef = inject(DestroyRef);

	constructor() {
		const queryChanges$ = toObservable(this.query).pipe(tap(() => this.currentPage.set(1)));

		merge(queryChanges$, this.pageTrigger$)
			.pipe(
				debounceTime(400),
				tap(() => {
					this.isLoading.set(true);
					this.errorMessage.set(null);
				}),
				switchMap(() => {
					const q = this.query().trim();
					if (q.length < 2) {
						this.isLoading.set(false);
						return of<DriverLedgerSearchPage | null>(null);
					}
					return this.api.searchEntries(q, this.currentPage(), this.pageSize()).pipe(
						catchError(() => {
							this.errorMessage.set('Błąd wyszukiwania');
							return of<DriverLedgerSearchPage | null>(null);
						}),
						finalize(() => this.isLoading.set(false)),
					);
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((page) => {
				if (!page) {
					this.rows.set([]);
					this.totalItems.set(0);
					this.matData.data = [];
					return;
				}
				this.rows.set(page.items);
				this.totalItems.set(page.total);
				this.matData.data = page.items;
			});
	}

	onPageChange(event: PageEvent): void {
		this.currentPage.set(event.pageIndex + 1);
		this.pageSize.set(event.pageSize);
		this.pageTrigger$.next();
	}

	toggleOriginalDocument(row: DriverLedgerSearchRow, event: Event): void {
		event.stopPropagation();

		const nextValue = !row.hasOriginalDocument;
		this.applyOriginalDocument(row.id, nextValue);

		this.entryApi
			.toggleOriginalDocument(row.driverId, row.id, nextValue)
			.pipe(
				catchError(() => {
					this.applyOriginalDocument(row.id, !nextValue);
					this.infobox.error('Błąd podczas aktualizacji oryginału');
					return EMPTY;
				}),
			)
			.subscribe();
	}

	openEdit(row: DriverLedgerSearchRow): void {
		if (!row.isMine) {
			return;
		}

		const entryForDialog: DriverLedgerEntry = {
			id: row.id,
			driverId: row.driverId,
			date: row.date,
			amount: row.amount,
			currency: row.currency,
			amountPln: row.amountPln,
			exchangeRate: null,
			amountEur: row.amountEur,
			exchangeRateEur: null,
			targetCurrency: row.targetCurrency,
			categoryId: row.categoryId,
			categoryName: row.categoryName,
			description: row.description,
			paymentDate: row.paymentDate,
			status: row.status,
			hasOriginalDocument: row.hasOriginalDocument,
			isMine: row.isMine,
			createdAt: row.createdAt,
			updatedAt: row.updatedAt,
			createdBy: row.createdBy,
			updatedBy: row.updatedBy,
			vehicleId: row.vehicleId,
			vehicleName: row.vehicleName,
			vehicleRegistrationNumber: row.vehicleRegistrationNumber,
			commission: null,
			lines: row.lines ?? [],
		};

		const dialogRef = this.dialog.open(DriverLedgerFormComponent, {
			width: '680px',
			data: { driverId: row.driverId, entry: entryForDialog } as DriverLedgerFormDialogData,
		});

		dialogRef.afterClosed().subscribe((result: DriverLedgerFormDialogResult | null) => {
			if (result?.action !== 'update') return;
			this.entryApi
				.updateEntry(row.driverId, row.id, result.payload)
				.pipe(
					catchError((err: { status?: number }) => {
						const msg =
							err?.status === 403
								? 'Tylko autor może edytować ten wpis'
								: 'Błąd podczas aktualizacji wpisu';
						this.infobox.error(msg);
						return EMPTY;
					}),
				)
				.subscribe(() => {
					this.infobox.success('Wpis został zaktualizowany');
					this.pageTrigger$.next();
				});
		});
	}

	deleteEntry(row: DriverLedgerSearchRow, event: Event): void {
		event.stopPropagation();

		if (!row.isMine) {
			return;
		}

		const dialogRef = this.dialog.open(DriverLedgerDeleteConfirmComponent, {
			width: '400px',
		});

		dialogRef.afterClosed().subscribe((confirmed: boolean) => {
			if (!confirmed) return;
			this.entryApi
				.deleteEntry(row.driverId, row.id)
				.pipe(
					catchError((err: { status?: number }) => {
						const msg =
							err?.status === 403 ? 'Tylko autor może usunąć ten wpis' : 'Błąd podczas usuwania wpisu';
						this.infobox.error(msg);
						return EMPTY;
					}),
				)
				.subscribe(() => {
					this.infobox.success('Wpis został usunięty');
					this.pageTrigger$.next();
				});
		});
	}

	private applyOriginalDocument(rowId: number, value: boolean): void {
		this.rows.update((items) =>
			items.map((item) => (item.id === rowId ? { ...item, hasOriginalDocument: value } : item)),
		);
		this.matData.data = this.rows();
	}
}
